import { useEffect, useState } from 'react';
import { Box, Button, ButtonBase, Stack, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DateRangeIcon from '@mui/icons-material/DateRange';
import StyledDatePicker from '@/components/date&time/StyledDatePicker';
import { localDateFromString } from '@/utils/businessDay';
import { REPORT_PRESETS, ReportPreset } from '@/utils/salesReport';

export interface ReportRangeValue {
  preset: ReportPreset;
  startDate: string;
  endDate: string;
}

interface ReportPresetBarProps {
  value: ReportRangeValue;
  onPresetSelect: (preset: Exclude<ReportPreset, 'custom'>) => void;
  onCustomApply: (startDate: string, endDate: string) => void;
}

/**
 * Hızlı seçim (madde 10): Bugün / Dün / Son 7 Gün / Son 30 Gün / Son 1 Yıl /
 * Özel Tarih — web POS "Raporlar" ile aynı sıra ve etiketler. Az seçenek
 * olduğu için açılır liste değil kutu butonlar.
 */
const ReportPresetBar = ({
  value,
  onPresetSelect,
  onCustomApply
}: ReportPresetBarProps) => {
  const { t } = useTranslation();
  const [customOpen, setCustomOpen] = useState(value.preset === 'custom');
  const [tempStart, setTempStart] = useState<Date | null>(
    localDateFromString(value.startDate)
  );
  const [tempEnd, setTempEnd] = useState<Date | null>(
    localDateFromString(value.endDate)
  );

  useEffect(() => {
    setTempStart(localDateFromString(value.startDate));
    setTempEnd(localDateFromString(value.endDate));
  }, [value.startDate, value.endDate]);

  const handleSelect = (preset: ReportPreset) => {
    if (preset === 'custom') {
      setCustomOpen(true);
      return;
    }
    setCustomOpen(false);
    onPresetSelect(preset);
  };

  const handleApply = () => {
    if (!tempStart || !tempEnd) return;
    onCustomApply(
      format(tempStart, 'yyyy-MM-dd'),
      format(tempEnd, 'yyyy-MM-dd')
    );
  };

  return (
    <Box>
      <Box
        role="radiogroup"
        aria-label={t('filters.date.presets')}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            lg: 'repeat(6, minmax(0, 1fr))'
          },
          gap: 1
        }}
      >
        {REPORT_PRESETS.map((preset) => {
          const selected =
            preset === 'custom'
              ? value.preset === 'custom' || customOpen
              : value.preset === preset && !customOpen;
          const label =
            preset === 'custom'
              ? t('filters.date.custom.date')
              : t(`filters.date.${preset}`);
          return (
            <ButtonBase
              key={preset}
              role="radio"
              aria-checked={selected}
              onClick={() => handleSelect(preset)}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: selected
                  ? theme.palette.primary.main
                  : theme.palette.divider,
                boxShadow: selected
                  ? `0 0 0 1px ${theme.palette.primary.main}`
                  : 'none',
                backgroundColor: selected
                  ? alpha(theme.palette.primary.main, 0.08)
                  : theme.palette.background.paper,
                color: selected
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
                fontWeight: selected ? 700 : 500,
                fontSize: 14,
                transition: theme.transitions.create([
                  'border-color',
                  'background-color',
                  'box-shadow'
                ]),
                '&:hover': {
                  borderColor: theme.palette.primary.main
                }
              })}
            >
              {preset === 'custom' && <DateRangeIcon fontSize="small" />}
              {label}
              {selected && preset !== 'custom' && (
                <CheckCircle fontSize="small" color="primary" />
              )}
            </ButtonBase>
          );
        })}
      </Box>

      {customOpen && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'flex-end' }}
          sx={{ mt: 2 }}
        >
          <Box sx={{ flex: 1 }}>
            <StyledDatePicker
              label={t('filters.date.start.date')}
              selected={tempStart}
              onChange={setTempStart}
              maxDate={tempEnd || undefined}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <StyledDatePicker
              label={t('filters.date.end.date')}
              selected={tempEnd}
              onChange={setTempEnd}
              minDate={tempStart || undefined}
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={!tempStart || !tempEnd}
            sx={{ minWidth: 140, height: 40 }}
          >
            {t('filters.date.apply.filter')}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default ReportPresetBar;
